import { translate } from "../i18n";
import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Pause, Play } from "@phosphor-icons/react";
export function LoopFilm({
  src,
  poster,
  className = "",
  label = translate("Video della stampa"),
  eager = false
}) {
  const video = useRef(null);
  const reduced = useReducedMotion();
  const [visible, setVisible] = useState(eager);
  const [requested, setRequested] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const saveData = typeof navigator !== "undefined" && navigator.connection?.saveData;
  const mobile = typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;
  const wantsPlay = requested === null ? !reduced && !saveData && !mobile : requested;
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), {
      threshold: .05
    });
    if (video.current) observer.observe(video.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const element = video.current;
    if (!element) return;
    const sync = () => {
      if (visible && wantsPlay && !document.hidden && !failed) {
        setLoaded(true);
        if (loaded) element.play().catch(() => setPlaying(false));
      } else element.pause();
    };
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => document.removeEventListener("visibilitychange", sync);
  }, [visible, wantsPlay, loaded, failed]);
  const toggle = () => {
    if (playing) {
      setRequested(false);
      video.current?.pause();
    } else {
      setRequested(true);
      setLoaded(true);
      video.current?.play().catch(() => setPlaying(false));
    }
  };
  return <div className={`loop-film ${className}`}>
    <video ref={video} src={loaded ? src : undefined} poster={poster} muted loop playsInline preload="none" aria-label={label} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onError={() => {
      setFailed(true);
      setPlaying(false);
    }} />
    <div className="film-shade" aria-hidden="true" />
    {!failed && <button type="button" className="film-control" onClick={toggle} aria-label={`${playing ? translate("Pausa") : translate("Riproduci")}: ${label}`}>{playing ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}<span>{playing ? translate("Pausa video") : translate("Riproduci video")}</span></button>}
  </div>;
}
export default function CinematicHero({
  onCta
}) {
  const reduced = useReducedMotion();
  const hero = useRef(null);
  const {
    scrollYProgress
  } = useScroll({
    target: hero,
    offset: ["start start", "end start"]
  });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 65]);
  return <section ref={hero} id="top" className="studio-hero" aria-label={translate("Pedrazzini Print 365, stampa e personalizzazione")}>
    <div className="studio-topline"><span>{translate("LOCARNO, SVIZZERA")}</span><span>{translate("STAMPA \xB7 IDEE \xB7 PERSONALIT\xC0")}</span></div>
    <div className="studio-headline">
      <motion.h1 initial={reduced ? false : {
        y: 35
      }} animate={{
        y: 0
      }} transition={{
        duration: .8
      }}>{translate("La tua idea.")}<br /><em>{translate("Lascia il segno.")}</em></motion.h1>
      <div className="studio-pitch"><p>{translate("Carta, colore, carattere.")}<br />{translate("Diamo forma a quello")}<br />{translate("che ti rende diverso.")}</p><button onClick={onCta} className="studio-cta">{translate("Il tuo preventivo")}<strong>{translate("in 24 ore")}</strong><ArrowUpRight size={30} /></button></div>
    </div>
    <div className="studio-media">
      <div className="studio-art"><motion.img style={reduced ? undefined : {
          y: imageY,
          scale: 1.12
        }} src="/images/print-art.webp" alt={translate("Composizione creativa di carte colorate, biglietti e stampati")} width="1536" height="1024" /><span className="studio-art-label">{translate("DALL\u2019IDEA ALLA MATERIA.")}</span></div>
      <div className="studio-live"><LoopFilm eager src="/video/printing-press.mp4" poster="/video/printing-press.jpg" label="Macchina da stampa in movimento" /><span className="studio-live-label">{translate("\xC8 QUI CHE")}<br />{translate("PRENDE FORMA.")}</span></div>
    </div>
    <div className="studio-caption"><span>{translate("Piccole tirature. Grandi possibilit\xE0.")}</span><button onClick={() => document.getElementById('servizi')?.scrollIntoView({
        behavior: reduced ? 'auto' : 'smooth'
      })}>{translate("Esplora la stampa ")}<ArrowDown size={20} /></button></div>
  </section>;
}
